import styled from "styled-components";
import { TRouteItem } from "src/components/FinaLayout/types";
import MenuItem from "./Item";
import { useLocation } from "react-router";
import { line } from "src/theme/ui";

/**
 * A group of nav rows.
 *
 * Football groups the rail with hairline rules rather than uppercase
 * headings: the grouping stays legible, but it costs no vertical space and
 * nothing to read. The headings this replaces ("ANALYTICS", "INVEST",
 * "CASH", "ACCOUNT") also had nothing to say that the icons beneath them
 * were not already saying.
 */

const Divider = styled.div`
  height: 1px;
  margin: 9px 2px;
  background: ${line.soft};
`;

/**
 * Goal-Based and Trading are not finished, and are hidden until they are —
 * except on integration, which is where they are being tested.
 *
 * This used to read:
 *
 *     process.env.NODE_ENV === "production" &&
 *     email !== "<one developer's personal gmail address>" &&
 *     (item.key === "GoalBased" || item.key === "Brokerage")
 *
 * which tied the decision to how the bundle was built rather than to where it
 * runs, and shipped a personal address in the client bundle to every
 * customer. Integration is a production build, so the two sections were
 * hidden in the one environment meant for trying them, while anyone running
 * the dev server saw them and assumed they were live.
 *
 * ENVIRONMENT_NAME is already a build argument — "INTG", "UAT", "PROD" — so
 * nothing new has to be wired into a deployment for this, and production is
 * untouched: it is not INTG, so it keeps hiding them.
 */
const UNRELEASED_KEYS = ["GoalBased", "Brokerage"];

const isUnreleased = (key: string) => UNRELEASED_KEYS.includes(key);

const canSeeUnreleased = () => {
  /* A developer running the app locally sees everything, as before. */
  if (process.env.NODE_ENV === "development") return true;

  return (
    (process.env.REACT_APP_ENVIRONMENT_NAME ?? "").toUpperCase() === "INTG"
  );
};

const ItemGroup = ({
  items,
  collapse,
  divider,
}: {
  title?: string;
  items: Array<TRouteItem>;
  borderTop?: string;
  collapse: boolean;
  /** A rule above this group, standing in for its heading. */
  divider?: boolean;
}) => {
  const location = useLocation();

  return (
    <div>
      {divider && <Divider aria-hidden="true" />}
      {items.map((item) => {
        if (isUnreleased(item.key) && !canSeeUnreleased()) return null;
        return (
          <MenuItem
            to={item.action ? undefined : item.path}
            onClick={item.action}
            content={item.name}
            key={item.key}
            icon={item.icon}
            collapse={collapse}
            $isActive={location.pathname === item.path}
          />
        );
      })}
    </div>
  );
};
export { ItemGroup as default };
